import { useTheme, alpha } from "@mui/material/styles";
import { useMemo } from "react";

/**
 * Chart colour roles.
 *
 * Deliberately narrow: these dashboards use single-series charts (magnitude and
 * trend), so there is no categorical palette to cycle through — one accent hue
 * plus the reserved status colours.
 *
 * Status hexes are fixed and never themed, so a status colour always means the
 * same thing in both modes. `warning` sits below 3:1 on the light surface by
 * design, which is why every status mark ships with a visible label and value
 * rather than relying on colour alone.
 */
const STATUS_HEX = {
  good: "#0CA30C",
  warning: "#FAB219",
  critical: "#D03B3B",
};

// Timesheet / invoice states mapped onto those reserved roles. Anything not
// listed falls back to neutral ink, which never impersonates a status.
const STATUS_ROLE = {
  approved: "good",
  paid: "good",
  completed: "good",
  active: "good",
  pending: "warning",
  overdue: "critical",
  rejected: "critical",
  cancelled: "critical",
};

export function useChartTokens() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return useMemo(() => {
    const series = theme.palette.primary.main;
    const neutral = isDark ? "#94A3B8" : "#64748B";

    return {
      isDark,
      // Single accent series. Validated >= 5.5:1 against both surfaces.
      series,
      seriesFillFrom: alpha(series, isDark ? 0.34 : 0.22),
      seriesFillTo: alpha(series, 0),
      // Recessive chrome — gridlines are hairlines, never competing with marks.
      grid: theme.palette.border.subtle,
      axis: theme.palette.border.default,
      axisInk: neutral,
      surface: theme.palette.background.paper,
      neutral,
      status: STATUS_HEX,
      statusFor: (value) => {
        const role = STATUS_ROLE[String(value ?? "").toLowerCase().trim()];
        return role ? STATUS_HEX[role] : neutral;
      },
    };
  }, [theme, isDark]);
}

export default useChartTokens;
